import React from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import './AboutPage.css';

const TEAM = [
  { name: 'Mandeep Kaur',      role: 'Leader' },
  { name: 'Peihong Zou',       role: 'Backend Leader' },
  { name: 'Anh Tran',          role: 'Backend Developer' },
  { name: 'Esha Srivastava',   role: 'Frontend Developer' },
  { name: 'Senaya Attanayake', role: 'Frontend Developer' },
  { name: 'Tristan Lee',       role: 'Backend Developer' },
];

function InitialAvatar({ name, className = '' }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase())
    .join('');

  return (
    <div className={`avatar-ring ${className}`} aria-hidden="true">
      <div className="avatar-initial">{initials}</div>
    </div>
  );
}

export default function AboutPage() {
  return (
    <div className="about-container">
      <NavBar />

      {/* Hero */}
      <section className="about-hero container" aria-labelledby="about-title">
        <h1 id="about-title" className="about-title">About Us</h1>
        <p className="about-subtitle">Empowering Logistics Education Through Simulations</p>
      </section>

      {/* Intro */}
      <section className="about-intro container">
        <p>
          At <span className="highlight">LogiSim</span>, we believe that learning logistics should
          be as dynamic and strategic as the industry itself. That's why we've built a platform
          that uses interactive simulations to teach complex logistics concepts in a hands-on and
          engaging way.
        </p>
        <p>
          Whether you are a student, educator, or professional, LogiSim offers tools to visualize
          and solve real-world supply chain challenges — from vehicle routing to multi-modal
          transportation.
        </p>
      </section>

      {/* Stats */}
      <section className="stats-band" aria-label="Platform statistics">
        <div className="container stats-row">
          <div className="stat">
            <div className="stat-number">5+</div>
            <div className="stat-label">Interactive Modules</div>
          </div>
          <div className="stat">
            <div className="stat-number">1000+</div>
            <div className="stat-label">Simulations Run</div>
          </div>
          <div className="stat">
            <div className="stat-number">A+</div>
            <div className="stat-label">Student Feedback</div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="mission-section container" aria-labelledby="mission-title">
        <h2 id="mission-title" className="section-title">Our Mission</h2>
        <p className="mission-text">
          To democratize logistics education by making it intuitive, accessible, and simulation-driven.
        </p>

        <div className="pill-grid">
          <span className="pill">Real-world logistics problem sets</span>
          <span className="pill">Beginner → Advanced learning paths</span>
          <span className="pill">Data-driven & visually rich experiences</span>
          <span className="pill">Scenario-based skill practice</span>
        </div>
      </section>

      {/* What we offer */}
      <section className="offer-cards container" aria-label="What we offer">
        <article className="offer-card">
          <h3>Hands-on Learning</h3>
          <p>Explore VRP, TSP, SPP, and TP with interactive flows and instant feedback.</p>
        </article>
        <article className="offer-card">
          <h3>For Class & Self-Study</h3>
          <p>Great for classrooms, labs, and solo learners with clear guidance and examples.</p>
        </article>
        <article className="offer-card">
          <h3>Industry Relevance</h3>
          <p>Simulations mirror real supply-chain challenges across modes and constraints.</p>
        </article>
      </section>

      {/* Team */}
      <section className="team-section container" aria-labelledby="team-title">
        <h2 id="team-title" className="section-title">Our Team</h2>
        <p className="team-subtitle">A focused team combining design, engineering, and logistics.</p>

        <div className="team-grid">
          {TEAM.map((m) => (
            <article className="team-card" key={m.name}>
              <InitialAvatar name={m.name} />
              <div className="member-text">
                <h3 className="member-name">{m.name}</h3>
                <span className="member-badge" aria-label={`Role: ${m.role}`}>{m.role}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-ribbon" aria-label="Get started">
        <div className="container cta-row">
          <div>
            <h3>Start Exploring Our Modules</h3>
            <p>Practice core logistics concepts with interactive simulations.</p>
          </div>
          <Link to="/simulations" className="cta-button">Explore Simulations</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}